/**
 * =============================================================================
 * Route table — **Application / UI composition (not a domain)**
 * =============================================================================
 * DDD note:
 * - Route configuration is **infrastructure for navigation**; it wires React
 *   components (mostly lazy) to URLs. Business rules (who may see what) stay
 *   in the auth bounded context (`authorizationSlice` + future policies).
 * - Feature screens should eventually live under `src/domains/<context>/ui/pages`
 *   and be imported here via their domain’s **public** `index.ts` barrel.
 *
 * Current scope (intentionally minimal): **shell home + settings** so CRA
 * builds succeed while `Header` + `MainLayout` remain the visual contract.
 * =============================================================================
 */

import React from 'react';
import { RouteEnum } from './routeEnum';
import { paths } from './paths';
import { AppRoute, RoutesByRole, DefaultRoutes } from './routes.types';
import {
  IconDashboard,
  IconClients,
  IconCategories,
  IconFaq,
  IconNotifications,
  IconSettings,
  IconSubscription,
} from '../components/Icons';

/** Default landing route after sign-in (per role prefix). */
const DashboardPage = React.lazy(
  () => import('../../domains/dashboard/ui/pages/DashboardPage'),
);
const ClientsPage = React.lazy(
  () => import('../../domains/clients/ui/pages/ClientsPage'),
);
const CategoriesPage = React.lazy(
  () => import('../../domains/category/ui/pages/CategoriesPage'),
);
const FaqPage = React.lazy(
  () => import('../../domains/faq/ui/pages/FaqPage'),
);
const FaqFormPage = React.lazy(
  () => import('../../domains/faq/ui/pages/FaqFormPage'),
);
const ChangelogPage = React.lazy(
  () => import('../../domains/changelog/ui/pages/ChangelogPage'),
);
const ChangelogFormPage = React.lazy(
  () => import('../../domains/changelog/ui/pages/ChangelogFormPage'),
);
const LegalPage = React.lazy(
  () => import('../../domains/legal/ui/pages/LegalPage'),
);
const CompanyStockOwnersPage = React.lazy(
  () => import('../../domains/clients/ui/pages/CompanyStockOwnersPage'),
);
const NotificationsPage = React.lazy(
  () => import('../../domains/notifications/ui/pages/NotificationsPage'),
);
const SettingsPage = React.lazy(
  () => import('../../domains/settings/ui/pages/SettingsPage'),
);
const InboxListPage = React.lazy(
  () => import('../../domains/inbox/ui/pages/InboxListPage'),
);
const InboxThreadPage = React.lazy(
  () => import('../../domains/inbox/ui/pages/InboxThreadPage'),
);
const FeedbackListPage = React.lazy(
  () => import('../../domains/feedback/ui/pages/FeedbackListPage'),
);
const BillingLayout = React.lazy(
  () => import('../../domains/billing/ui/pages/BillingLayout'),
);
const BillingPlansPage = React.lazy(
  () => import('../../domains/billing/plans/ui/pages/PlansPage'),
);
const BillingPaymentOptionsPage = React.lazy(
  () => import('../../domains/billing/payment-options/ui/pages/PaymentOptionsPage'),
);
const BillingWalletsListPage = React.lazy(
  () => import('../../domains/billing/wallet/ui/pages/WalletsListPage'),
);
const BillingSubscriptionsPage = React.lazy(
  () => import('../../domains/billing/subscriptions/ui/pages/SubscriptionsPage'),
);
const BillingSubscriptionDetailPage = React.lazy(
  () => import('../../domains/billing/subscriptions/ui/pages/SubscriptionDetailPage'),
);
const ClientLegalOfferScopePage = React.lazy(
  () => import('../../domains/legal-pages/ui/pages/ClientLegalOfferScopePage'),
);
const ClientLegalOfferEditorPage = React.lazy(
  () => import('../../domains/legal-pages/ui/pages/ClientLegalOfferEditorPage'),
);
const PublicOfferPage = React.lazy(
  () => import('../../domains/public-offer/ui/pages/PublicOfferPage'),
);

const SignIn = React.lazy(() => import('../../authorization/SignIn/SignIn'));
const SignUp = React.lazy(() => import('../../authorization/sign-up'));
const ForgotPassword = React.lazy(
  () => import('../../authorization/ForgotPassword/ForgotPassword'),
);
const ResetPassword = React.lazy(
  () => import('../../authorization/ResetPassword/ResetPassword'),
);
const ClientSignIn = React.lazy(
  () => import('../../authorization/Client/SignIn'),
);

const ownerRoutes: AppRoute[] = [
  {
    element: DashboardPage,
    route: `/owner${paths.dashboard}`,
    pageTitle: 'dashboard.page-title',
    pathKey: 'dashboard',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath => currentPath.includes(`/owner${paths.dashboard}`),
    renderIcon: currentPath => (
      <IconDashboard
        isActive={currentPath.includes(`/owner${paths.dashboard}`)}
      />
    ),
  },
  {
    element: ClientsPage,
    route: `/owner${paths.clients}`,
    pageTitle: 'navigation.clients',
    pathKey: 'clients',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath => currentPath.includes(`/owner${paths.clients}`),
    renderIcon: currentPath => (
      <IconClients
        isActive={currentPath.includes(`/owner${paths.clients}`)}
      />
    ),
  },
  {
    element: CategoriesPage,
    route: `/owner${paths.categories}`,
    pageTitle: 'navigation.categories',
    pathKey: 'categories',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath =>
      currentPath.includes(`/owner${paths.categories}`),
    renderIcon: currentPath => (
      <IconCategories
        isActive={currentPath.includes(`/owner${paths.categories}`)}
      />
    ),
  },
  {
    element: FaqPage,
    route: `/owner${paths.faq}`,
    pageTitle: 'navigation.faq',
    pathKey: 'faq',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath => currentPath.includes(`/owner${paths.faq}`),
    renderIcon: currentPath => (
      <IconFaq isActive={currentPath.includes(`/owner${paths.faq}`)} />
    ),
  },
  {
    element: ChangelogPage,
    route: `/owner${paths.changelog}`,
    pageTitle: 'navigation.changelog',
    pathKey: 'changelog',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath =>
      currentPath.includes(`/owner${paths.changelog}`),
    renderIcon: currentPath => (
      <IconSettings
        isActive={currentPath.includes(`/owner${paths.changelog}`)}
      />
    ),
  },
  {
    element: LegalPage,
    route: `/owner${paths.legal}`,
    pageTitle: 'navigation.legal',
    pathKey: 'legal',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath => currentPath.includes(`/owner${paths.legal}`),
    renderIcon: currentPath => (
      <IconSettings isActive={currentPath.includes(`/owner${paths.legal}`)} />
    ),
  },
  {
    element: FaqFormPage,
    route: `/owner${paths.faqCreate}`,
    pageTitle: 'faq.createTitle',
    pathKey: 'faqCreate',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: FaqFormPage,
    route: `/owner${paths.faqEdit}`,
    pageTitle: 'faq.editTitle',
    pathKey: 'faqEdit',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: ChangelogFormPage,
    route: `/owner${paths.changelogCreate}`,
    pageTitle: 'changelog.createTitle',
    pathKey: 'changelogCreate',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: ChangelogFormPage,
    route: `/owner${paths.changelogEdit}`,
    pageTitle: 'changelog.editTitle',
    pathKey: 'changelogEdit',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: CompanyStockOwnersPage,
    route: '/owner/company/:id',
    pageTitle: 'navigation.companyStockOwners',
    pathKey: 'companyStockOwners',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes('/owner/company/'),
    renderIcon: currentPath => (
      <IconClients
        isActive={currentPath.includes('/owner/company/')}
      />
    ),
  },
  {
    element: ClientLegalOfferScopePage,
    route: `/owner${paths.clientLegalOffer}`,
    pageTitle: 'legalOffer.pageTitle',
    pathKey: 'clientLegalOffer',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: ClientLegalOfferEditorPage,
    route: `/owner${paths.clientLegalOfferEditor}`,
    pageTitle: 'legalOffer.pageTitle',
    pathKey: 'clientLegalOfferEditor',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: InboxListPage,
    route: `/owner${paths.tickets}`,
    pageTitle: 'navigation.messages',
    pathKey: 'messages',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath =>
      currentPath.includes(`/owner${paths.tickets}`) ||
      currentPath.includes(`/owner${paths.feedback}`),
    renderIcon: currentPath => (
      <IconNotifications
        isActive={
          currentPath.includes(`/owner${paths.tickets}`) ||
          currentPath.includes(`/owner${paths.feedback}`)
        }
      />
    ),
    children: [
      {
        element: InboxListPage,
        route: `/owner${paths.tickets}`,
        pageTitle: 'navigation.tickets',
        pathKey: 'tickets',
        roles: ['owner'],
        requireAuth: true,
        isInNavigation: true,
        isActive: currentPath =>
          currentPath.includes(`/owner${paths.tickets}`),
      },
      {
        element: FeedbackListPage,
        route: `/owner${paths.feedback}`,
        pageTitle: 'navigation.feedback',
        pathKey: 'feedback',
        roles: ['owner'],
        requireAuth: true,
        isInNavigation: true,
        isActive: currentPath =>
          currentPath.includes(`/owner${paths.feedback}`),
      },
    ],
  },
  {
    element: BillingLayout,
    route: `/owner${paths.billing}`,
    pageTitle: 'navigation.billing',
    pathKey: 'billing',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath => currentPath.includes(`/owner${paths.billing}`),
    renderIcon: currentPath => (
      <IconSubscription
        isActive={currentPath.includes(`/owner${paths.billing}`)}
      />
    ),
    children: [
      {
        element: BillingPlansPage,
        route: `/owner${paths.billingPlans}`,
        pageTitle: 'navigation.billingPlans',
        pathKey: 'billingPlans',
        roles: ['owner'],
        requireAuth: true,
        isInNavigation: true,
        isActive: currentPath =>
          currentPath.includes(`/owner${paths.billingPlans}`),
      },
      {
        element: BillingPaymentOptionsPage,
        route: `/owner${paths.billingPaymentOptions}`,
        pageTitle: 'navigation.billingPaymentOptions',
        pathKey: 'billingPaymentOptions',
        roles: ['owner'],
        requireAuth: true,
        isInNavigation: true,
        isActive: currentPath =>
          currentPath.includes(`/owner${paths.billingPaymentOptions}`),
      },
      {
        element: BillingWalletsListPage,
        route: `/owner${paths.billingWallets}`,
        pageTitle: 'navigation.billingWallets',
        pathKey: 'billingWallets',
        roles: ['owner'],
        requireAuth: true,
        isInNavigation: true,
        isActive: currentPath => {
          const base = `/owner${paths.billingWallets}`;
          return currentPath.includes(base) && !/\/wallets\/\d+/.test(currentPath);
        },
      },
      {
        element: BillingSubscriptionsPage,
        route: `/owner${paths.billingSubscriptions}`,
        pageTitle: 'navigation.billingSubscriptions',
        pathKey: 'billingSubscriptions',
        roles: ['owner'],
        requireAuth: true,
        isInNavigation: true,
        isActive: currentPath => {
          const base = `/owner${paths.billingSubscriptions}`;
          return (
            currentPath.includes(base)
            && !/\/subscriptions\/\d+/.test(currentPath)
          );
        },
      },
    ],
  },
  {
    element: InboxThreadPage,
    route: `/owner${paths.feedbackThread}`,
    pageTitle: 'navigation.feedback',
    pathKey: 'feedbackThread',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes('/owner/feedback/'),
    renderIcon: currentPath => (
      <IconFaq isActive={currentPath.includes('/owner/feedback/')} />
    ),
  },
  {
    element: InboxThreadPage,
    route: `/owner${paths.ticketThread}`,
    pageTitle: 'navigation.tickets',
    pathKey: 'ticket',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes('/owner/ticket/'),
    renderIcon: currentPath => (
      <IconNotifications isActive={currentPath.includes('/owner/ticket/')} />
    ),
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settings}`,
    pageTitle: 'settings.page-title',
    pathKey: 'settings',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsProfile}`,
    pageTitle: 'settings.my-settings',
    pathKey: 'settingsProfile',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsAboutUs}`,
    pageTitle: 'settings.about-us',
    pathKey: 'settingsAboutUs',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsAdmin}`,
    pageTitle: 'settings.admin-management',
    pathKey: 'settingsAdmin',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsCompanySettings}`,
    pageTitle: 'settings.company-settings',
    pathKey: 'settingsCompanySettings',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: PublicOfferPage,
    route: `/owner${paths.settingsLegalOffer}`,
    pageTitle: 'settings.publicOffer.pageTitle',
    pathKey: 'settingsLegalOffer',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsIntegrations}`,
    pageTitle: 'settings.integrations',
    pathKey: 'settingsIntegrations',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsDeploy}`,
    pageTitle: 'settings.deploy',
    pathKey: 'settingsDeploy',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
  },
  {
    element: NotificationsPage,
    route: `/owner${paths.notifications}`,
    pageTitle: 'navigation.notifications',
    pathKey: 'notifications',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath =>
      currentPath.includes(`/owner${paths.notifications}`),
    renderIcon: currentPath => (
      <IconNotifications
        isActive={currentPath.includes(`/owner${paths.notifications}`)}
      />
    ),
  },
];

export const defaultRoutesByRole: DefaultRoutes = {
  client: `/owner${paths.dashboard}`,
  tenant: `/owner${paths.dashboard}`,
  owner: `/owner${paths.dashboard}`,
  none: RouteEnum.SIGN_IN,
};

export const routesByRole: RoutesByRole = {
  owner: ownerRoutes,
  tenant: ownerRoutes,
  client: ownerRoutes,
  none: [],
};

/** Routes that do not require `MainLayout` (auth screens). */
export const publicRoutes: AppRoute[] = [
  { element: SignIn, route: RouteEnum.SIGN_IN, requireAuth: false },
  { element: SignUp, route: RouteEnum.SIGN_UP, requireAuth: false },
  {
    element: ForgotPassword,
    route: RouteEnum.FORGOT_PASSWORD,
    requireAuth: false,
  },
  { element: ResetPassword, route: RouteEnum.RESET_PASSWORD, requireAuth: false },
  {
    element: ClientSignIn,
    route: RouteEnum.CLIENT_SIGN_IN,
    requireAuth: false,
  },
];
